// Sound effects utilities for the AI Tutor application
export class SoundEffects {
  private audioContext: AudioContext | null = null;
  private typingIntervalId: NodeJS.Timeout | null = null;
  private isTypingSoundEnabled = true;
  private spacebarBuffer: AudioBuffer | null = null;
  private isAudioLoaded = false;

  constructor() {
    // Initialize audio context only when needed
    this.initAudioContext();
    // Don't load spacebar sound immediately - wait for user interaction
  }

  private initAudioContext() {
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (error) {
      console.warn('Audio context not supported:', error);
      this.isTypingSoundEnabled = false;
    }
  }

  private async loadSpacebarSound() {
    if (!this.audioContext) {
      console.log('🚫 No audio context available for loading spacebar sound');
      return;
    }

    try {
      console.log('🔄 Loading spacebar.mp3...');
      // Load the spacebar MP3 file
      const response = await fetch('/spacebar.mp3'); // You'll need to place your MP3 in the public folder
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const arrayBuffer = await response.arrayBuffer();
      this.spacebarBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      this.isAudioLoaded = true;
      console.log('✅ Spacebar sound loaded successfully! Duration:', this.spacebarBuffer.duration.toFixed(2), 'seconds');
    } catch (error) {
      console.warn('❌ Could not load spacebar sound, falling back to synthetic sound:', error);
      this.isAudioLoaded = false;
    }
  }

  private async ensureAudioContext() {
    if (!this.audioContext) {
      console.log('🔄 Audio context not found, initializing...');
      this.initAudioContext();
    }
    
    if (this.audioContext && this.audioContext.state === 'suspended') {
      try {
        console.log('🔄 Resuming suspended audio context...');
        await this.audioContext.resume();
        console.log('✅ Audio context resumed');
      } catch (error) {
        console.warn('❌ Could not resume audio context:', error);
      }
    }

    // Load spacebar sound if we have a valid audio context and haven't loaded it yet
    if (this.audioContext && !this.isAudioLoaded && !this.spacebarBuffer) {
      await this.loadSpacebarSound();
    }
  }

  // Create a realistic typing sound using the spacebar MP3
  private createTypingSound() {
    if (!this.audioContext || !this.isTypingSoundEnabled) return;

    if (this.isAudioLoaded && this.spacebarBuffer) {
      console.log('🔊 Playing spacebar sound');
      // Use the loaded spacebar sound
      this.playSpacebarSound();
    } else {
      console.log('🔊 Playing synthetic sound (spacebar not loaded)');
      // Fallback to synthetic sound if audio not loaded
      this.createSyntheticTypingSound();
    }
  }

  private playSpacebarSound() {
    if (!this.audioContext || !this.spacebarBuffer) {
      console.log('❌ Cannot play spacebar sound - missing context or buffer');
      return;
    }

    const source = this.audioContext.createBufferSource();
    const gainNode = this.audioContext.createGain();
    
    // Trim silence and create a clean buffer
    const trimmedBuffer = this.trimSilence(this.spacebarBuffer);
    source.buffer = trimmedBuffer;
    
    source.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    
    // Realistic volume variation
    // Most keystrokes are quiet (0.1-0.3), but occasionally louder (0.4-0.7)
    const isLoudKeystroke = Math.random() < 0.15; // 15% chance of loud keystroke
    const baseVolume = isLoudKeystroke ? 0.4 + Math.random() * 0.3 : 0.1 + Math.random() * 0.2;
    
    // Slight pitch variation to simulate different keys
    const pitchVariation = 0.95 + Math.random() * 0.1; // ±5% pitch variation
    source.playbackRate.value = pitchVariation;
    
    gainNode.gain.setValueAtTime(baseVolume, this.audioContext.currentTime);
    
    console.log(`🎵 Playing spacebar - Volume: ${baseVolume.toFixed(2)}, Pitch: ${pitchVariation.toFixed(2)}`);
    
    source.start();
  }

  // Trim silence from the beginning and end of the audio buffer
  private trimSilence(buffer: AudioBuffer, threshold: number = 0.01): AudioBuffer {
    if (!this.audioContext) return buffer;

    const channelData = buffer.getChannelData(0);
    const sampleRate = buffer.sampleRate;
    
    // Find start of actual sound
    let startSample = 0;
    for (let i = 0; i < channelData.length; i++) {
      if (Math.abs(channelData[i]) > threshold) {
        startSample = Math.max(0, i - Math.floor(sampleRate * 0.002)); // Keep 2ms padding
        break;
      }
    }
    
    // Find end of actual sound
    let endSample = channelData.length - 1;
    for (let i = channelData.length - 1; i >= 0; i--) {
      if (Math.abs(channelData[i]) > threshold) {
        endSample = Math.min(channelData.length - 1, i + Math.floor(sampleRate * 0.002)); // Keep 2ms padding
        break;
      }
    }
    
    // Create new buffer with trimmed audio
    const trimmedLength = endSample - startSample + 1;
    const trimmedBuffer = this.audioContext.createBuffer(
      buffer.numberOfChannels, 
      trimmedLength, 
      sampleRate
    );
    
    // Copy trimmed data to new buffer
    for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
      const originalData = buffer.getChannelData(channel);
      const trimmedData = trimmedBuffer.getChannelData(channel);
      
      for (let i = 0; i < trimmedLength; i++) {
        trimmedData[i] = originalData[startSample + i];
      }
    }
    
    return trimmedBuffer;
  }

  // Fallback synthetic sound (original method)
  private createSyntheticTypingSound() {
    if (!this.audioContext || !this.isTypingSoundEnabled) return;

    // Create multiple oscillators for a more complex, realistic sound
    const oscillator1 = this.audioContext.createOscillator();
    const oscillator2 = this.audioContext.createOscillator();
    const noiseBuffer = this.createNoiseBuffer();
    const noiseSource = this.audioContext.createBufferSource();
    
    const gainNode1 = this.audioContext.createGain();
    const gainNode2 = this.audioContext.createGain();
    const noiseGain = this.audioContext.createGain();
    const masterGain = this.audioContext.createGain();
    
    // Connect nodes
    oscillator1.connect(gainNode1);
    oscillator2.connect(gainNode2);
    noiseSource.connect(noiseGain);
    
    gainNode1.connect(masterGain);
    gainNode2.connect(masterGain);
    noiseGain.connect(masterGain);
    masterGain.connect(this.audioContext.destination);
    
    // Configure the sounds - lower frequency, more mechanical
    // Main body of the key press (lower frequency)
    oscillator1.frequency.setValueAtTime(150 + Math.random() * 50, this.audioContext.currentTime);
    oscillator1.type = 'square';
    
    // Higher harmonic for the "click" (still lower than before)
    oscillator2.frequency.setValueAtTime(300 + Math.random() * 100, this.audioContext.currentTime);
    oscillator2.type = 'triangle';
    
    // Set up the noise buffer for the "thunk" sound
    noiseSource.buffer = noiseBuffer;
    
    const now = this.audioContext.currentTime;
    
    // Master volume (much quieter)
    masterGain.gain.setValueAtTime(0, now);
    masterGain.gain.linearRampToValueAtTime(0.003, now + 0.005); // Very quick attack, very quiet
    masterGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.08); // Longer decay
    
    // Individual oscillator volumes
    gainNode1.gain.setValueAtTime(0.7, now); // Main key sound
    gainNode2.gain.setValueAtTime(0.3, now); // Click sound
    noiseGain.gain.setValueAtTime(0.1, now); // Subtle noise
    
    // Quick frequency modulation for more realistic sound
    oscillator1.frequency.exponentialRampToValueAtTime(oscillator1.frequency.value * 0.8, now + 0.02);
    oscillator2.frequency.exponentialRampToValueAtTime(oscillator2.frequency.value * 0.9, now + 0.01);
    
    // Play the sounds
    oscillator1.start();
    oscillator2.start();
    noiseSource.start();
    
    oscillator1.stop(now + 0.08);
    oscillator2.stop(now + 0.05);
    noiseSource.stop(now + 0.03);
  }

  // Create a short noise buffer for the mechanical "thunk"
  private createNoiseBuffer(): AudioBuffer {
    if (!this.audioContext) throw new Error('Audio context not available');
    
    const bufferSize = this.audioContext.sampleRate * 0.02; // 20ms of noise
    const buffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
    const output = buffer.getChannelData(0);
    
    for (let i = 0; i < bufferSize; i++) {
      // Create filtered noise that decays quickly
      const decay = Math.exp(-i / (bufferSize * 0.3));
      output[i] = (Math.random() * 2 - 1) * decay * 0.1;
    }
    
    return buffer;
  }

  // Start playing typing sounds with realistic patterns to simulate natural typing
  public async startTypingSounds() {
    if (!this.isTypingSoundEnabled) {
      console.log('🔇 Sound effects disabled');
      return;
    }

    console.log('🎹 Starting typing sounds...');
    await this.ensureAudioContext();
    this.stopTypingSounds(); // Stop any existing typing sounds

    const playRealisticTyping = () => {
      // Simulate realistic typing patterns
      const typingBurst = this.generateTypingBurst();
      console.log('🎼 Generated burst with', typingBurst.length, 'keystrokes');
      this.playTypingBurst(typingBurst, 0);
    };

    // Start after a short delay
    this.typingIntervalId = setTimeout(playRealisticTyping, 300);
  }

  // Generate a realistic typing burst pattern
  private generateTypingBurst(): number[] {
    // More varied burst lengths for realistic typing
    const burstLength = 3 + Math.floor(Math.random() * 8); // 3-10 keystrokes per burst
    const intervals: number[] = [];
    
    for (let i = 0; i < burstLength; i++) {
      if (i === 0) {
        intervals.push(0); // First keystroke immediately
      } else {
        // Even faster inter-keystroke timing for more natural flow
        const baseInterval = 60 + Math.random() * 100; // 60-160ms between keys (faster)
        
        // Occasional pauses, but less frequent
        const pauseChance = Math.random();
        if (pauseChance < 0.1) { // 10% chance of thinking pause
          intervals.push(baseInterval + 400 + Math.random() * 600); // 0.5-1 second pause
        } else if (pauseChance < 0.25) { // 15% chance of brief hesitation
          intervals.push(baseInterval + 100 + Math.random() * 200); // 0.2-0.4s pause
        } else {
          intervals.push(baseInterval); // Normal fast typing speed
        }
      }
    }
    
    return intervals;
  }

  // Play a burst of typing sounds with specific intervals
  private playTypingBurst(intervals: number[], index: number) {
    if (index >= intervals.length) {
      // Shorter pauses between bursts for more active typing feel
      const nextBurstDelay = 400 + Math.random() * 800; // 0.4-1.2 seconds between bursts
      this.typingIntervalId = setTimeout(() => {
        if (this.typingIntervalId) { // Check if still active
          const nextBurst = this.generateTypingBurst();
          this.playTypingBurst(nextBurst, 0);
        }
      }, nextBurstDelay);
      return;
    }

    // Play current keystroke
    this.createTypingSound();

    // Schedule next keystroke
    if (index < intervals.length - 1) {
      this.typingIntervalId = setTimeout(() => {
        this.playTypingBurst(intervals, index + 1);
      }, intervals[index + 1]);
    } else {
      // This was the last keystroke in the burst
      this.playTypingBurst(intervals, index + 1);
    }
  }

  // Stop typing sounds
  public stopTypingSounds() {
    if (this.typingIntervalId) {
      clearTimeout(this.typingIntervalId);
      this.typingIntervalId = null;
    }
  }

  // Enable/disable sound effects
  public setSoundEnabled(enabled: boolean) {
    this.isTypingSoundEnabled = enabled;
    if (!enabled) {
      this.stopTypingSounds();
    }
  }

  // Check if sound is enabled
  public isSoundEnabled(): boolean {
    return this.isTypingSoundEnabled;
  }

  // Test method to play a single keystroke sound
  public async testSingleKeystroke() {
    await this.ensureAudioContext();
    this.createTypingSound();
  }

  // Test method to play a sequence of keystrokes
  public async testTypingSequence(count: number = 10) {
    await this.ensureAudioContext();
    
    let keyCount = 0;
    const playKey = () => {
      if (keyCount < count) {
        this.createTypingSound();
        keyCount++;
        // Faster intervals between keys like real typing
        const interval = 100 + Math.random() * 200; // 100-300ms
        setTimeout(playKey, interval);
      }
    };
    
    playKey();
  }

  // Test method to play realistic typing bursts
  public async testRealisticTyping(durationSeconds: number = 15) {
    await this.ensureAudioContext();
    
    const endTime = Date.now() + (durationSeconds * 1000);
    
    const playBurst = () => {
      if (Date.now() < endTime) {
        const burst = this.generateTypingBurst();
        this.playTypingBurst(burst, 0);
        
        // Shorter delays between bursts for more active feel
        setTimeout(() => {
          if (Date.now() < endTime) {
            playBurst();
          }
        }, 600 + Math.random() * 1000); // 0.6-1.6 second pauses
      }
    };
    
    playBurst();
  }

  // Cleanup method
  public dispose() {
    this.stopTypingSounds();
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}

// Singleton instance for global use
let soundEffectsInstance: SoundEffects | null = null;

export const getSoundEffects = (): SoundEffects => {
  if (!soundEffectsInstance) {
    soundEffectsInstance = new SoundEffects();
  }
  return soundEffectsInstance;
};

// Cleanup function for app teardown
export const cleanupSoundEffects = () => {
  if (soundEffectsInstance) {
    soundEffectsInstance.dispose();
    soundEffectsInstance = null;
  }
};